<script>
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkDialog from '@components/general/AkDialog';
import AkInputText from '@components/v2/input/AkInputText';
import GaView from '@components/v2/layout/GaView';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import {Icon} from '@iconify/vue';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import itemLabelService from '@services/itemLabelService';

export default {
  components: {Icon, GaView, GaListView, AkDatatablePagination, AkButtonAction, AkInputText, AkDialog},
  mixins: [loaderMixin, messageMixin, randomRef, utilsMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.resiliation_reason',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      item: {
        type: 'RESILIATION_REASON',
        enabled: true,
        label: '',
      },
    };
  },
  validations() {
    return {
      item: {
        label: {required},
      },
    };
  },
  mounted() {
    itemLabelService.itemLabels({type: 'RESILIATION_REASON'}).then((data) => {
      this.list = data;
      this.loading = false;
    });
  },
  methods: {
    create() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogCreate.init();
        return;
      }

      this.loading = true;
      itemLabelService
        .create(this.item)
        .then((data) => {
          this.success(this.$t('resiliation_reason.added'));
          this.list.push(data);
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.loading = false;
          this.$refs.dialogCreate.hide();
        });
    },
    update() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogEdit.init();
        return;
      }

      this.loading = true;
      itemLabelService
        .update(this.item)
        .then((data) => {
          this.replaceInListById(this.list, data);
          this.success(this.$t('resiliation_reason.updated'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.loading = false;
          this.$refs.dialogEdit.hide();
        });
    },
    delete() {
      itemLabelService
        .delete(this.item)
        .then((data) => {
          this.removeInListById(this.list, data.id);
          this.success(this.item.label + ' a bien été supprimé');
        })
        .catch((e) => {
          if (e.response.status === 406) {
            this.addError('Cette raison de résiliation est utilisée, et ne peut être supprimée');
          } else {
            this.addError(this.$t('unknow_error'));
          }
        });
    },

    openCreateDialog() {
      this.item = {
        type: 'RESILIATION_REASON',
        enabled: true,
        label: '',
      };
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item};
      this.$refs.dialogEdit.show();
    },
    openDeleteDialog(item) {
      this.item = item;
      this.$refs.dialogDelete.show();
    },
    reorder(event) {
      const item = this.list[event.dragIndex];
      this.loading = true;
      itemLabelService.reorderPosition(item, event.dropIndex - event.dragIndex).then(() => {
        this.reorderInListByIdAndDelta(this.list, item.id, event.dropIndex - event.dragIndex);
        this.loading = false;
      });
    },
  },
};
</script>

<template>
  <GaView :ref="ref" :title="$t('resiliation_reason.list')">
    <template #action>
      <div class="flex justify-end">
        <AkButtonAction :label="$t('add')" little @click="openCreateDialog()" />
      </div>
    </template>

    <template #content>
      <GaListView>
        <template #content>
          <AkDatatablePagination can-change-order @row-reorder="reorder" :lazy="false" :loading="loading" :value="list">
            <template #empty>
              {{ $t('resiliation_reason.empty') }}
            </template>
            <Column :header="$t('item_label.label')">
              <template #body="slotProps">
                {{ slotProps.data.label }}
              </template>
            </Column>
            <Column style="width: 100px">
              <template #body="slotProps">
                <div class="flex justify-end gap-2">
                  <span class="md:hover:cursor-pointer" @click="openEditDialog(slotProps.data)">
                    <i class="ga-icon ga-edit text-2xl text-grey-900" />
                  </span>
                  <span class="md:hover:cursor-pointer" @click="openDeleteDialog(slotProps.data)">
                    <i class="ga-icon ga-trash text-2xl text-red" />
                  </span>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('resiliation_reason.add')"
        :validate-label="$t('add')"
        width="450px"
        @validate="create()">
        <div class="flex flex-col gap-2">
          <AkInputText v-model="item.label" :label="$t('resiliation_reason.item')" :validator="v$.item.label" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('resiliation_reason.update')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="flex flex-col gap-2">
          <AkInputText v-model="item.label" :label="$t('resiliation_reason.item')" :validator="v$.item.label" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogDelete"
        :auto-hide-on-validate="true"
        :cancel-label="$t('no')"
        :title="$t('delete_dialog')"
        :validate-label="$t('yes')"
        width="450px"
        @validate="this.delete()">
        <div class="flex items-center gap-2">
          <Icon class="text-red" icon="fluent:warning-20-regular" width="26" />
          <span>{{ $t('resiliation_reason.confirm_delete') }}</span>
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
